import { useRef } from 'react'
import { connect } from 'react-redux'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { InformationCircleMiniIcon } from '@calendly/ui/components/icons'
const colorIconSecondary = 'var(--colorIconSecondary, var(--coreColorNeutral600))'
import { useFlag } from '@calendly/feature-check-react'

import { currentUserSelector } from 'selectors/user'
import { useBooleanState } from 'hooks/useBooleanState'
import { useI18n } from 'locales'
import { useGetCalendarAccountsQuery } from 'root-api'

import { useMutualAvailabilityEnabled } from './use_mutual_availability_enabled'
import Popup from './popup'
import {
  Panel,
  Header,
  ClickableHeader,
  InfoIconContainer,
  Avatar,
  Title,
  Content,
  ScrollContainer,
  PretendEventWarning,
  RedesignHeader,
  RedesignClickableHeader,
  RedesignHeaderInner,
  RedesignHeaderText,
  RedesignTitle,
  RedesignSubtitle,
  RedesignHeaderActions,
  RedesignAvatar,
  RedesignFlexContent,
  RedesignPhoneRoot,
  RedesignPhoneHeader,
  RedesignPhoneClickableHeader
} from './styles'
import ErrorWarning from './error_warning'
import { useContainerPosition } from './use_container_position'
import EventList from './invitee_event_list'
import CalendarsLine from './calendars_line'

const RedesignScheduleHeaderContent = ({
  avatarUrl,
  email,
  locale,
  i18n,
  hasCalendarConnected
}) => (
  <RedesignHeaderInner>
    <RedesignHeaderText>
      <RedesignTitle>
        {i18n.t('publisher.mutual_availability.header.your_schedule', { locale })}
      </RedesignTitle>
      {email ? <RedesignSubtitle>{email}</RedesignSubtitle> : null}
    </RedesignHeaderText>
    <RedesignHeaderActions>
      {hasCalendarConnected && (
        <InformationCircleMiniIcon color={colorIconSecondary} size="x-large" />
      )}
      {avatarUrl ? (
        <RedesignAvatar src={avatarUrl} alt={i18n.t('booking.avatar', { locale })} />
      ) : null}
    </RedesignHeaderActions>
  </RedesignHeaderInner>
)

const MutualAvailabilitySidebar = ({
  avatarUrl,
  inviteePublisherError,
  locale,
  isPretending,
  userEmail
}) => {
  const { i18n } = useI18n()
  const bookingAppRedesign = useFlag('booking_app_redesign', false)
  const isEnabled = useMutualAvailabilityEnabled()
  const contentRef = useRef()
  const screenPosition = useContainerPosition(contentRef)
  const [isPopupOpen, openPopup, closePopup] = useBooleanState(false)
  const isPhone = useDeviceType('isPhone')
  const { isLoading, data: calendarAccounts } = useGetCalendarAccountsQuery(undefined, { skip: !isEnabled })

  if (isLoading) return null

  const hasCalendarConnected = !!calendarAccounts?.length
  const HeaderComponent = hasCalendarConnected ? ClickableHeader : Header
  const RedesignDesktopHeaderComponent = hasCalendarConnected ? RedesignClickableHeader : RedesignHeader
  const RedesignPhoneHeaderComponent = hasCalendarConnected ? RedesignPhoneClickableHeader : RedesignPhoneHeader

  if (isPhone && isPretending) {
    return (
      <PretendEventWarning>
        Calendly Admins can not see users' calendar events while in pretend mode.
      </PretendEventWarning>
    )
  }

  if (!isEnabled) return null

  if (isPhone && !isPretending) {
    return bookingAppRedesign ? (
      <RedesignPhoneRoot>
        <RedesignPhoneHeaderComponent onClick={hasCalendarConnected ? openPopup : undefined}>
          <RedesignScheduleHeaderContent
            avatarUrl={avatarUrl}
            email={userEmail}
            locale={locale}
            i18n={i18n}
            hasCalendarConnected={hasCalendarConnected}
          />
        </RedesignPhoneHeaderComponent>
        <div ref={contentRef}>
          {inviteePublisherError ? <ErrorWarning /> : (
            <EventList containerScreenPosition={screenPosition} variant="redesign" />
          )}
        </div>
        {isPopupOpen && <Popup onClose={closePopup} />}
      </RedesignPhoneRoot>
    ) : (
      <EventList containerScreenPosition={screenPosition} />
    )
  }

  if (bookingAppRedesign) {
    return (
      <Panel bookingAppRedesign>
        <RedesignDesktopHeaderComponent onClick={hasCalendarConnected ? openPopup : undefined}>
          <RedesignScheduleHeaderContent
            avatarUrl={avatarUrl}
            email={userEmail}
            locale={locale}
            i18n={i18n}
            hasCalendarConnected={hasCalendarConnected}
          />
        </RedesignDesktopHeaderComponent>
        {isPretending
          ? (
            <PretendEventWarning>
              Calendly Admins can not see users' calendar events while in pretend mode.
            </PretendEventWarning>
          ) : (
            <RedesignFlexContent ref={contentRef}>
              <ScrollContainer>
                {inviteePublisherError ? <ErrorWarning /> : (
                  <EventList containerScreenPosition={screenPosition} variant="redesign" />
                )}
              </ScrollContainer>
            </RedesignFlexContent>
          )}
        {isPopupOpen && <Popup onClose={closePopup} />}
      </Panel>
    )
  }

  return (
    <Panel>
      <HeaderComponent onClick={hasCalendarConnected ? openPopup : null}>
        {hasCalendarConnected && (
          <InfoIconContainer>
            <InformationCircleMiniIcon color={colorIconSecondary} size="x-large" />
          </InfoIconContainer>
        )}
        {avatarUrl && <Avatar src={avatarUrl} alt={i18n.t('booking.avatar', { locale })} />}
        <Title>{i18n.t('publisher.mutual_availability.header.schedule_from', { locale })}</Title>
        <CalendarsLine />
      </HeaderComponent>
      {isPretending
        ? (
          <PretendEventWarning>
            Calendly Admins can not see users' calendar events while in pretend mode.
          </PretendEventWarning>
        ) : (
          <Content ref={contentRef} isPhone={isPhone}>
            <ScrollContainer>
              {
                inviteePublisherError
                  ? <ErrorWarning />
                  : <EventList containerScreenPosition={screenPosition} />
              }
            </ScrollContainer>
          </Content>
        )}
      {isPopupOpen && <Popup onClose={closePopup} />}
    </Panel>
  )
}

const mapStateToProps = (state) => {
  const currentUser = currentUserSelector(state)

  return {
    avatarUrl: currentUser && currentUser.avatar_url,
    locale: currentUser && currentUser.locale,
    userEmail: currentUser && currentUser.email,
    inviteePublisherError: state.calendar.inviteePublisherError,
    isPretending: currentUser && currentUser.is_pretending
  }
}

export const MutualAvailability = connect(mapStateToProps)(MutualAvailabilitySidebar)
